(() => {
/* Fraction of a mobile step the incoming slide spends moving. The remainder is
   taken off the front as a hold, so the slide that just landed sits still and
   full screen before the next one starts climbing over it. Mirrors
   mobile_transition_height in sections/horizontal-multiple-banner.liquid. */
const MOBILE_TRAVEL = 0.7;
const MOBILE_HOLD = 1 - MOBILE_TRAVEL;

/** @param {number} value */
const clamp01 = (value) => Math.min(1, Math.max(0, value));

class HorizontalMultipleBanner extends HTMLElement {
  #abortController = new AbortController();
  #frame = 0;
  #activeIndex = -1;
  #motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  #mobileQuery = window.matchMedia('(max-width: 919px)');

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    this.slides = [...this.querySelectorAll('[data-banner-slide]')];
    this.productImages = [...this.querySelectorAll('[data-product-image]')];
    this.pin = this.querySelector('[data-banner-pin]');
    this.counter = this.querySelector('[data-banner-counter]');
    this.productCard = this.querySelector('[data-product-card]');
    this.previousButton = this.querySelector('[data-banner-previous]');
    this.nextButton = this.querySelector('[data-banner-next]');
    this.scrollTarget = this.#findScrollTarget();

    if (!this.pin || !this.slides.length) return;

    const { signal } = this.#abortController;
    this.scrollTarget.addEventListener('scroll', this.#requestUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#requestUpdate, { passive: true, signal });
    this.#motionQuery.addEventListener('change', this.#requestUpdate, { signal });
    this.#mobileQuery.addEventListener('change', this.#requestUpdate, { signal });

    this.previousButton?.addEventListener('click', () => this.#jump(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.#jump(1), { signal });
    this.addEventListener(
      'shopify:block:select',
      (event) => {
        const slide = event.target.closest?.('[data-banner-slide]');
        const index = this.slides.indexOf(slide);
        if (index >= 0) this.#scrollToIndex(index);
      },
      { signal }
    );

    this.dataset.sliderReady = 'true';
    this.#update();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    cancelAnimationFrame(this.#frame);
  }

  #requestUpdate = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #update = () => {
    const count = this.slides.length;
    const reducedMotion = this.#motionQuery.matches;

    if (count < 2) {
      this.style.setProperty('--banner-progress', '0');
      this.#setActive(0);
      return;
    }

    const rect = this.getBoundingClientRect();
    const mobile = this.#mobileQuery.matches;
    const step = this.pin.offsetHeight;
    const scrollViewportTop = this.scrollTarget === window ? 0 : this.scrollTarget.getBoundingClientRect().top;
    const rawProgress = Math.min(
      count - 1,
      Math.max(0, -(rect.top - scrollViewportTop) / Math.max(1, step))
    );
    const progress = reducedMotion ? Math.round(rawProgress) : rawProgress;
    this.style.setProperty('--banner-progress', progress.toFixed(4));

    this.slides.forEach((slide, index) => {
      const media = slide.querySelector('[data-banner-media]');
      const contentWrap = slide.querySelector('.horizontal-multiple-banner__content-wrap');
      const content = slide.querySelector('[data-banner-content]');

      if (mobile) {
        /* Park waiting slides a whole viewport down, not 70% of one. At 70% the
           next slide still covered the bottom third of the screen while the
           current one was supposedly settled, so only the last slide — which has
           nothing behind it — ever looked full screen. */
        const distance = Math.max(0, index - progress);
        const offset = Math.min(1, distance / MOBILE_TRAVEL) * 100;
        if (media) media.style.transform = `translate3d(0, ${offset}%, 0)`;

        const active = Math.min(count - 1, Math.floor(progress));
        /* 0 for the whole hold, then 0->1 across the part of the step where the
           slides actually move, so the copy stays put while its banner does. */
        const move = clamp01((progress - active - MOBILE_HOLD) / MOBILE_TRAVEL);
        const tint = index === active ? 1 - move : index === active + 1 ? move : 0;
        const opacity =
          index === active
            ? 1 - Math.min(1, move / 0.45)
            : index === active + 1
              ? Math.min(1, Math.max(0, (move - 0.5) / 0.5))
              : 0;

        if (content) {
          content.style.opacity = String(opacity);
          content.style.transform = 'none';
        }
        if (contentWrap) contentWrap.style.opacity = String(tint);
      } else {
        const reveal = index === 0 ? 1 : Math.min(1, Math.max(0, progress - (index - 1)));
        const active = Math.min(count - 1, Math.floor(progress));
        const handoff = Math.min(1, Math.max(0, progress - active));
        const opacity =
          index === active
            ? 1 - Math.min(1, handoff / 0.45)
            : index === active + 1
              ? Math.min(1, Math.max(0, (handoff - 0.55) / 0.45))
              : 0;
        if (media) media.style.transform = `translate3d(0, ${(1 - reveal) * 100}%, 0)`;
        if (content) {
          content.style.opacity = String(opacity);
          content.style.transform = `translate3d(0, ${(1 - opacity) * 22}px, 0)`;
        }
        if (contentWrap) contentWrap.style.removeProperty('opacity');
      }
    });

    /* The product image fills on exactly the same 0->1 fraction that carries the
       banner behind it, so it tops out at the instant that banner lands. Mobile
       used to compress the fill into the first 0.35 of a step, which finished
       the product image while its banner was still ~65% of a step from home. */
    this.productImages.forEach((image, index) => {
      const reveal = index === 0 ? 1 : Math.min(1, Math.max(0, progress - (index - 1)));
      image.style.clipPath = `inset(${(1 - reveal) * 100}% 0 0 0)`;
    });

    this.#setActive(Math.min(count - 1, Math.max(0, Math.round(progress))));
  };

  #setActive(index) {
    if (index === this.#activeIndex) return;
    this.#activeIndex = index;

    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === index;
      slide.toggleAttribute('data-active', active);
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
      slide.querySelectorAll('a, button').forEach((element) => {
        element.tabIndex = active ? 0 : -1;
      });
    });

    this.productImages.forEach((image, imageIndex) => {
      image.setAttribute('aria-hidden', String(imageIndex !== index));
    });

    const activeLink = this.slides[index]?.dataset.bannerLink;
    const activeLabel = this.slides[index]?.dataset.bannerLabel;
    if (this.productCard) {
      if (activeLink) {
        this.productCard.href = activeLink;
        this.productCard.removeAttribute('aria-disabled');
        this.productCard.setAttribute('aria-label', activeLabel ? `Shop ${activeLabel}` : 'Shop this campaign');
      } else {
        this.productCard.removeAttribute('href');
        this.productCard.setAttribute('aria-disabled', 'true');
      }
    }

    if (this.previousButton) this.previousButton.disabled = index === 0;
    if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;

    if (this.counter) {
      this.counter.textContent = `${String(index + 1).padStart(2, '0')} / ${String(this.slides.length).padStart(2, '0')}`;
    }
  }

  #jump(direction) {
    const count = this.slides.length;
    if (count < 2) return;

    const targetIndex = Math.min(count - 1, Math.max(0, this.#activeIndex + direction));
    this.#scrollToIndex(targetIndex);
  }

  #scrollToIndex(targetIndex) {
    const count = this.slides.length;
    if (count < 2) return;

    const step = this.pin.offsetHeight;
    const behavior = this.#motionQuery.matches ? 'auto' : 'smooth';

    if (this.scrollTarget === window) {
      const sectionTop = this.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: sectionTop + step * targetIndex, behavior });
    } else {
      const targetRect = this.scrollTarget.getBoundingClientRect();
      const sectionOffset = this.getBoundingClientRect().top - targetRect.top + this.scrollTarget.scrollTop;
      this.scrollTarget.scrollTo({ top: sectionOffset + step * targetIndex, behavior });
    }
  }
  #findScrollTarget() {
    let ancestor = this.parentElement;

    while (ancestor && ancestor !== document.body) {
      const style = getComputedStyle(ancestor);
      if (/(auto|scroll|overlay)/.test(style.overflowY) && ancestor.scrollHeight > ancestor.clientHeight) {
        return ancestor;
      }
      ancestor = ancestor.parentElement;
    }

    return window;
  }
}

if (!customElements.get('indian-terrain-season')) {
  customElements.define('indian-terrain-season', HorizontalMultipleBanner);
}
})();
